import { useQuery } from '@tanstack/react-query';

import { fetchOperator, UnauthenticatedError } from '../api/session';

/** Shared key, so the shell and `/sign-in` resolve the session exactly once. */
export const operatorQueryKey = ['auth', 'operator'] as const;

/**
 * Resolve the signed-in operator.
 *
 * `retry` deliberately gives up immediately on a 401: that is not a flaky
 * request, it is the answer, and retrying it three times would leave an
 * unauthenticated employee staring at a spinner before the redirect. Genuine
 * transport failures still get one retry, because the difference between "you
 * are signed out" and "the API blipped" is the difference between bouncing
 * someone to Atelier and telling them to try again.
 */
export function useOperator() {
    return useQuery({
        queryKey: operatorQueryKey,
        queryFn: ({ signal }) => fetchOperator(signal),
        retry: (failureCount, error) =>
            !(error instanceof UnauthenticatedError) && failureCount < 1,
        staleTime: 5 * 60_000,
        // Load-bearing. Under the default `networkMode: 'online'`, a failure
        // React Query classifies as a connectivity problem does not become an
        // error — it parks the query in `pending`/`paused` until the online
        // manager says otherwise. The gate renders a spinner on `isPending`, so
        // that combination is an employee watching "Checking your Atelier
        // session…" forever with nothing to click. Verified against a stopped
        // API: `fetchStatus` sat at `paused` indefinitely with `navigator.onLine`
        // reporting true. 'always' makes the failure surface as an error, which
        // is what `SessionUnavailable` and its retry button exist for.
        networkMode: 'always',
    });
}
